import { Controller } from '@hotwired/stimulus';
import Papa from 'papaparse';
import { Module, TabulatorFull as Tabulator } from 'tabulator-tables';
import { showMessage, confirmMessage } from './message_controller';
import { openImage } from './modal_controller';

function getCsrfToken() {
  return document.querySelector('meta[name="csrf-token"]').getAttribute('content');
}

// Maps two different sets of columns to each other
// used for RBF forms, that have different attribute names
class Mapper {
  map;
  reverse_map;

  // initializes the mapping for both directions
  constructor(mapping, category) {
    if (mapping?.[category]) {
      this.map = mapping[category];
      this.reverse_map = this.flip(mapping[category]);
    }
  }

  // creates a flipped mapping from the given one
  // only used in the initialization
  flip(mapping) {
    return Object.fromEntries(
      Object.entries(mapping).map(([key, value]) => {
        const v = structuredClone(value);
        const k = value.field;
        v.field = key;
        return [k, v];
      })
    );
  }

  // fetch field info from the mapping via a key
  key(value) {
    return this.getFieldInfo(value, this.map);
  }

  // fetch field info from the reverse mapping via a key
  reverseKey(value) {
    return this.getFieldInfo(value, this.reverse_map);
  }

  // find our field with some fuzzy logic, if not found, return the original value
  // if we have a map but can not find the value, we assume it is s field that changed it's name
  // after we opened the vault and removed the postfix
  getFieldInfo(value, map) {
    if (map) {
      if (map[value]) {
        return map[value];
      }
      if (map[value.slice(0, -2)]) {
        const tmp = structuredClone(map[value.slice(0, -2)]);
        tmp.field = `${tmp.field}_e`;
        return tmp;
      }
    } else {
      return { field: value };
    }
  }
}

// implement filter with ransacker names
// list not exhausted
Tabulator.extendModule('filter', 'filters', {
  eq: (filterVal, rowVal, rowData, filterParams) => {
    return filterVal === rowVal;
  },
  lt: (filterVal, rowVal, rowData, filterParams) => {
    return filterVal < rowVal;
  },
  gt: (filterVal, rowVal, rowData, filterParams) => {
    return filterVal > rowVal;
  },
  lteq: (filterVal, rowVal, rowData, filterParams) => {
    return filterVal <= rowVal;
  },
  gteq: (filterVal, rowVal, rowData, filterParams) => {
    return filterVal >= rowVal;
  },
  not_eq: (filterVal, rowVal, rowData, filterParams) => {
    return filterVal !== rowVal;
  },
  matches: (filterVal, rowVal, rowData, filterParams) => {
    return filterVal.matches(rowVal);
  },
});

// custom Filter module that implements a really crude interface to add filters to the table
// the idea was to have remote and local filters
// remote filters can be done by the database
// and local filter can only be applied in the browser
// because the fields are encrypted on the server
class Filter {
  column_select;
  filter_select;
  filter_input;
  filter_count;
  filter_list;
  table;
  remote_filter;
  local_filter;

  constructor(
    table,
    column_select,
    filter_select,
    filter_input,
    filter_count,
    filter_list,
    add_filter_button,
    clear_filter_button,
    columns
  ) {
    // if there are already filters, use them, the filter is recreated
    // after the vault was opened and column names changed in the process
    if (table.filter) {
      this.remote_filter = table.filter.remote_filter;
      this.local_filter = table.filter.local_filter;
    } else {
      this.remote_filter = [];
      this.local_filter = [];
    }

    this.table = table;

    column_select.innerHTML = '';

    // we add each column to the available columns list
    for (const c of columns) {
      // there could be an "action" column for specific rbf data tables, that contains no data
      // just buttons which we want to skip
      if (this.table.options?.rbf && c === 'actions') {
        continue;
      }

      const option = new Option();

      // if we have a mapping, we want to filter by the mapped fields
      const mapping = this.table.options.mapper.reverseKey(c);

      if (mapping) {
        option.value = mapping.field;
        option.encrypted = mapping.encrypted;
      }
      option.text = c;

      // if the column is encrypted and the vault is not open,
      // we disable filtering for this column
      if (c.endsWith('_e')) {
        option.disabled = true;
      } else {
        // if the vault is open, but the column is encrypted, we only allow local filtering
        if (option.encrypted) {
          option.text = `${option.text} (local)`;
        } else {
          option.text = `${option.text} (remote)`;
        }
      }

      column_select.add(option);
    }

    // mapping to ransacker filters from "mathy" symbols
    const filter = [
      { text: '=', value: 'eq' },
      { text: '<', value: 'lt' },
      { text: '>', value: 'gt' },
      { text: '<=', value: 'lteq' },
      { text: '>=', value: 'gteq' },
      { text: '!=', value: 'not_eq' },
      { text: '~', value: 'matches' },
    ];

    // wipe whatever was in the filter
    filter_select.innerHTML = '';

    // add all options
    for (const f of filter) {
      filter_select.add(new Option(f.text, f.value));
    }

    // store all our elements for later use
    this.column_select = column_select;
    this.filter_select = filter_select;
    this.filter_input = filter_input;
    this.filter_count = filter_count;
    this.filter_list = filter_list;
    this.filter_count.parentElement.onclick = () => {
      this.filter_list.classList.toggle('hidden');
      this.column_select.classList.toggle('hidden');
      this.filter_select.classList.toggle('hidden');
      this.filter_input.classList.toggle('hidden');
      add_filter_button.classList.toggle('hidden');
      clear_filter_button.classList.toggle('hidden');
    };
    add_filter_button.onclick = this.addFilter.bind(this);
    clear_filter_button.onclick = this.clearFilters.bind(this);
  }

  // remove all applied filters
  clearFilters() {
    this.local_filter = [];
    this.remote_filter = [];
    this.table.options.filterMode = 'remote';
    this.table.clearFilter();
    this.updateFilterCount();
    this.updateFilterList();
  }

  // take the currently selected filter from the UI and apply it
  // if the column is encrypted it is added to the local filters
  addFilter() {
    const filter = `${this.column_select.value} ${this.filter_select.value} ${this.filter_input.value}`;
    if (this.column_select.selectedOptions[0].encrypted) {
      this.local_filter.push({
        field: this.table.options.mapper.key(this.column_select.value).field,
        type: this.filter_select.value,
        value: this.filter_input.value,
      });
    } else {
      this.remote_filter.push({
        field: this.column_select.value,
        type: this.filter_select.value,
        value: this.filter_input.value,
      });
    }
    this.triggerFilter();
  }

  // partition filters in remote and local filters based on the column type (encrypted/not encrypted)
  // disable columns for local filters if vault is locked, listen to unlock and unlock the filter
  triggerFilter() {
    // apply remote filters
    this.table.options.filterMode = 'remote';
    this.table.setFilter(this.remote_filter);

    // apply local filters
    this.table.options.filterMode = 'local';
    this.table.setFilter(this.local_filter);

    this.table.module('filter').filterList = this.remote_filter;

    this.filter_input.value = '';
    this.updateFilterCount();
    this.updateFilterList();
  }

  // updates the visual list of applied filter for the user
  updateFilterList() {
    // clean list
    this.filter_list.innerHTML = '';

    // function that adds the element and also a remove button for this specific filter
    const addElementToList = (index, filter, list) => {
      const div = document.createElement('div');
      div.classList.add('m-1', 'cursor-pointer');
      div.innerText = `${filter.field} ${filter.type} ${filter.value}`;
      this.filter_list.append(div);

      const remove = document.createElement('span');
      remove.innerText = '✖ ';
      remove.onclick = () => {
        list.splice(index, 1);
        this.triggerFilter();
      };
      div.prepend(remove);
    };

    // apply for remote filters
    for (const [index, filter] of this.remote_filter.entries()) {
      addElementToList(index, filter, this.remote_filter);
    }

    // apply for local filters
    for (const [index, filter] of this.local_filter.entries()) {
      addElementToList(index, filter, this.local_filter);
    }
  }

  // update visual counter of applied filters
  updateFilterCount() {
    const filter_count = this.remote_filter.length + this.local_filter.length;
    if (filter_count > 0) {
      this.filter_count.innerText = filter_count;
      this.filter_count.classList.remove('hidden');
    } else {
      this.filter_count.classList.add('hidden');
    }
  }
}

// this module does all the work of splitting the custom column into it's own columns
// renders server-side html columns (view_data_rbf's Sales actions, states and eligibility)
// put encrypted images behind a separate load button
// set's the correct formatting for each column
// adds an editing mode to update specific values
class SelectableColumnsModule extends Module {
  static moduleName = 'selectableColumns';

  initialize() {
    // we add a custom handler for the ajax response
    this.table.options.ajaxResponse = async (...[, params, response]) => {
      const final_data = [];
      for (const row of response.data) {
        // split off the custom column
        const { custom, ...data } = row;

        // stringify any value in the custom column that is a json otherwise it will just show: [Object object]
        if (custom && typeof custom === 'object') {
          for (const key of Object.keys(custom)) {
            if (typeof custom[key] === 'object' && custom[key] !== null) {
              custom[key] = JSON.stringify(custom[key]);
            }
          }
        }

        const combined_row = { ...data, ...(custom || {}) };
        // will contain the columns renamed after decryption
        const new_column = {};

        // decrypt columns if the vault is open
        if (window.vault?.is_open()) {
          for (const key in combined_row) {
            if (key.endsWith('_e')) {
              // if we have an encrypted blob, we just copy the value
              // it can be viewed later via the custom image format
              if (combined_row[key]?.match('http.*encrypted_blob')) {
                new_column[key.slice(0, -2)] = combined_row[key];
              } else {
                new_column[key.slice(0, -2)] = await window.vault.decryptMessage(combined_row[key]);
              }
            } else {
              new_column[key] = combined_row[key];
            }
          }
          final_data.push(new_column);
        } else {
          final_data.push(combined_row);
        }
      }

      // "Showing x of y" under the filter bar: x is the count the endpoint makes for last_page anyway. y comes
      // with the page for the Sales table (total_rows), else from the first unfiltered load, so filtering never
      // costs a second count
      const hint = this.table.rowCountHint;
      if (hint && response.count != null) {
        if (this.table.options.total_rows != null)
          this.table.totalRows = this.table.options.total_rows;
        else if (!params?.filter?.length) this.table.totalRows = response.count;
        if (this.table.totalRows != null) {
          hint.textContent = hint.dataset.template
            .replace('%{count}', response.count.toLocaleString())
            .replace('%{total}', this.table.totalRows.toLocaleString());
          hint.classList.remove('hidden');
        }
      }

      // the download asks to confirm the number of rows it is about to download
      const exportForm = this.table.exportForm;
      if (exportForm && response.count != null) {
        exportForm.dataset.turboConfirm = exportForm.dataset.confirmTemplate.replace(
          '%{count}',
          response.count.toLocaleString()
        );
      }

      // the Sales table's download button tells how many rows it downloads
      const download = this.table.downloadSales;
      if (download && response.count != null) {
        download.dataset.tooltipText = download.dataset.labelTemplate.replace(
          '%{number}',
          response.count.toLocaleString()
        );
      }

      return {
        data: final_data,
        errors: response.errors,
        last_page: response.last_page,
      };
    };

    // subscribe to table events
    this.subscribe('table-initialized', this.addButtonAction.bind(this));
    this.subscribe('columns-loaded', this.initializeColumn.bind(this));
    this.subscribe('cell-value-changed', this.cellUpdated.bind(this));
    this.subscribe('data-loaded', this.mapData.bind(this));
    this.subscribe('column-format', this.formatColumn.bind(this));
    this.subscribe('data-loading', this.dataLoading.bind(this));
    // Tabulator redraws when the table's width changes (widescreen toggle, a hidden tab shown), so the caps follow it
    this.subscribe('table-redrawing', this.capColumnWidths.bind(this));
    if (this.table.exportFilter)
      this.subscribe('data-refreshed', this.updateExportFilter.bind(this));
  }

  // the export form (projects/_view_data_table) posts what the table shows: its filters and sorting
  updateExportFilter() {
    this.table.exportFilter.value = JSON.stringify(this.table.getCurrentFilters());
  }

  // 'fitData' sizes a column to its longest value; Tabulator's maxWidth takes pixels only, so the
  // 30% of the table is worked out here. Encrypted columns are too big even then and get 200px
  maxColumnWidth(column) {
    const field = column.getField();
    if (field.endsWith('_p') || field.endsWith('_e') || field.includes('photo')) return 200;

    // 0 while the table is hidden in another tab, i.e. no cap until it is shown and redrawn
    return Math.floor(this.table.element.clientWidth * 0.3);
  }

  capColumnWidths() {
    for (const column of this.table.getColumns()) {
      column._column.setMaxWidth(this.maxColumnWidth(column));
    }
  }

  // TODO: figure out why I did this
  dataLoading(data, params, config, silent) {
    if (this.table.filter) {
      this.table.options.filterMode = 'remote';
      this.table.module('filter').filterList = this.table.filter.remote_filter;
    }
    return true;
  }

  // add format to the columns
  formatColumn(column, title, el) {
    const mapping = this.table.options.mapper.reverseKey(title);
    // if the vault is not open, we ignore the encrypted columns
    if (window.vault && !window.vault.is_open() && mapping?.encrypted) {
      return title;
    }

    // html_columns hold server-rendered markup (view_data_rbf's Sales: action buttons, state pills, eligibility)
    // with nothing to sort on. Else the formatter comes from the mapping (RBF form)
    if (this.table.options.html_columns?.includes(title)) {
      column.definition.headerSort = false;
      column.definition.formatter = 'html';
      if (this.table.options.center_columns?.includes(title)) {
        column.definition.hozAlign = 'center';
        column.definition.headerHozAlign = 'center';
      }
    } else {
      column.definition.formatter = mapping?.format;
    }

    if (this.table.options.rbf) {
      // the pills make the Sales table's rows taller than a line of text; keep every cell in the middle
      column.definition.vertAlign = 'middle';
      // the Sales table's row buttons speak for themselves
      if (title === 'actions') return '';
    }

    return title;
  }

  // use the mapper to map the data to the correct columns
  mapData(data) {
    // TODO: figure out why I did this
    // I might have done this to apply the filter because the code is run after this is executed
    if (this.table.filter) {
      this.table.options.filterMode = 'local';
      this.table.module('filter').filterList = this.table.filter.local_filter;
    }

    // if we have no mapper, return the data as is
    if (!this.table.options.mapper) {
      return data.data;
    }

    const mapped_data = [];

    for (const row of data.data) {
      const mapped_row = {};

      for (const [key, value] of Object.entries(row)) {
        // TODO: we need some kind of mechanism to map _e or _p or _b(?) specifically
        if (key.endsWith('_p')) {
          continue;
        }

        const mapped = this.table.options.mapper.key(key);
        if (mapped) {
          mapped_row[mapped.field] = value;
        }
      }
      mapped_data.push(mapped_row);
    }

    return mapped_data;
  }

  // change the background color of the cell to indicate it has been edited
  cellUpdated(cell) {
    cell.getElement().style['background-color'] = 'rgb(93, 239, 236)';
    cell.edited = true;
  }

  addButtonAction() {
    if (this.table.options.update_path === null) {
      this.table.editButton.classList.add('hidden');
    }
    this.table.editButton.onclick = () => {
      this.editMode();
    };
    this.table.cancelButton.onclick = () => {
      this.readMode();
    };
    this.table.commitButton.onclick = () => {
      this.saveChanges();
    };
  }

  // creates a csv with the whole row with any edited cell and
  // sends it to the server to get ingested
  saveChanges() {
    const updated_rows = this.table
      .getRows()
      .filter((row) => row.getCells().filter((cell) => cell._cell.edited).length > 0);
    const data = updated_rows.map((row) => row.getData());

    if (data.length === 0) {
      showMessage('No changes found');
      return;
    }

    const csv = Papa.unparse(data);

    const fd = new FormData();
    fd.append('source[data_category]', this.table.options.data_category);
    fd.append('source[file]', new Blob([csv]), 'update.csv');
    fd.append('replace', 'true'); // edited records: the server only logs dropped columns like <field>_e

    fetch(this.table.options.update_path, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': getCsrfToken(),
      },
      body: fd,
    })
      .then((response) => {
        if (response.ok) {
          window.location = response.url;
        } else {
          showMessage('Error occurred while submitting changes.');
        }
      })
      .catch((error) => {
        showMessage('Error occurred while submitting changes.');
        console.log('Submission error:', error);
      });
  }

  // switch to edit mode
  async editMode() {
    // warn the user, that edit mode without an open vault has its caveats
    if (window.vault && !window.vault.is_open()) {
      const confirmed = await confirmMessage(
        'Vault is locked, encrypted data not viewable and can only be completely overwritten.'
      );
      if (!confirmed) return;
    }

    const columns = this.table.getColumns();
    const data_table = this.table.options.data_table;

    // not all columns are updateable, we indicate locked columns with a lock icon and grey text
    for (const column of columns) {
      if (
        data_table[column.getDefinition().title]?.exclude_from_ingress ||
        data_table[this.table.options.mapper.reverseKey(column.getDefinition().title)?.field]
          ?.exclude_from_ingress
      ) {
        column.getDefinition().title = `${column.getDefinition().title} 🔒`;
        column.getDefinition().formatter = (...[cell, ,]) => {
          cell.getElement().classList.add('text-grey-4');
          return cell.getValue();
        };
      } else {
        column.getDefinition().editor = 'input';
      }
    }

    this.table.setColumns(columns.map((c) => c.getDefinition()));
    // display commit and cancel buttons
    this.table.editButton.classList.add('hidden');
    this.table.commitButton.classList.remove('hidden');
    if (this.table.editDescription) {
      this.table.editDescription.classList.remove('hidden');
    }
    this.table.cancelButton.classList.remove('hidden');
  }

  // switch to read mode
  readMode() {
    const columns = this.table.getColumns();
    const data_table = this.table.options.data_table;

    for (const column of columns) {
      // restore old values which were not committed
      for (const cell of column.getCells()) {
        if (cell._cell.edited) {
          cell.setValue(cell.getOldValue());
        }
      }
      if (data_table[column.getDefinition().field]?.exclude_from_ingress) {
        // remove the lock symbol from the title and revert the text color changes
        column.getDefinition().title = [...column.getDefinition().title].slice(0, -2).join('');
        column.getDefinition().formatter = (...[cell, ,]) => {
          cell.getElement().classList.remove('text-grey-4');
          return cell.getValue();
        };
      } else {
        column.getDefinition().editor = undefined;
      }
    }

    this.table.setColumns(columns.map((c) => c.getDefinition()));
    this.table.editButton.classList.remove('hidden');
    this.table.cancelButton.classList.add('hidden');
    if (this.table.editDescription) {
      this.table.editDescription.classList.add('hidden');
    }
    this.table.commitButton.classList.add('hidden');
  }

  // initialize column selection dropdown and the column filter
  initializeColumn() {
    // if there is no data, we have no columns, the end
    if (this.table.getColumns().length === 0) {
      return;
    }

    const active_selection = {};

    // check if we already have an active selection and use it if
    // the columns change for example after opening the vault
    if (this.table.column_filter) {
      for (const option of this.table.column_filter.options) {
        active_selection[option.innerText] = option.selected;
      }

      // remove the old filter dropdown
      if (this.table.column_filter.parentElement) {
        this.table.column_filter.parentElement.removeChild(this.table.column_filter);
      }
    }

    const column_select = document.createElement('select');
    column_select.multiple = true;

    for (const column of this.table.getColumns()) {
      if (this.table.options?.rbf && column.getDefinition().field === 'actions') {
        continue;
      }
      // we try to be faster than the onclick event if the user clicks on the column header,
      // that triggers the sort, to change the sort mode based on the data in the column
      // I found no better way to do this...
      const mapped = this.table.options.mapper.reverseKey(column.getDefinition().field);
      column.getElement().onmousedown = (_e) => {
        if (mapped.encrypted || column.getDefinition().field.endsWith('_e')) {
          this.table.options.sortMode = 'local';
          this.table.module('filter').filterList = this.table.filter.local_filter;
        } else {
          this.table.options.sortMode = 'remote';
        }
      };
      column._column.setMaxWidth(this.maxColumnWidth(column));

      // add all columns to the select element
      const option = new Option();
      option.innerText = column.getDefinition().field;

      if (option.innerText in active_selection) {
        option.selected = active_selection[option.innerText];
      } else {
        option.selected = true;
      }

      option.column = column;
      column_select.add(option);
    }

    // trigger the visibility change event if a column is selected or deselected
    column_select.onchange = () => {
      for (const option of column_select.options) {
        if (option.selected ^ option.column.isVisible()) {
          option.column.toggle();
          this.table.redraw();
        }
      }
    };

    this.table.column_filter = column_select;

    // the columns modal next to the filter bar lists a checkbox per column, below one that toggles them all
    column_select.hidden = true;
    const container = this.table.columnSelectContainer;
    const labelled = (text, checkbox) => {
      checkbox.type = 'checkbox';
      checkbox.className = 'rounded border-gray-300 text-primary focus:ring-primary';
      const label = document.createElement('label');
      label.className = 'flex items-center gap-2 py-1 text-sm cursor-pointer';
      label.append(checkbox, text);
      return label;
    };

    const toggleAll = document.createElement('input');
    const syncToggleAll = () => {
      const selected = Array.from(column_select.options).filter((option) => option.selected).length;
      toggleAll.checked = selected === column_select.options.length;
      toggleAll.indeterminate = selected > 0 && !toggleAll.checked;
    };

    const checkboxes = Array.from(column_select.options, (option) => {
      const checkbox = document.createElement('input');
      checkbox.checked = option.selected;
      checkbox.onchange = () => {
        option.selected = checkbox.checked;
        column_select.onchange();
        syncToggleAll();
      };
      return labelled(option.innerText, checkbox);
    });

    toggleAll.onchange = () => {
      for (const option of column_select.options) option.selected = toggleAll.checked;
      for (const label of checkboxes) label.querySelector('input').checked = toggleAll.checked;
      // one redraw for all columns instead of one per column
      this.table.blockRedraw();
      column_select.onchange();
      this.table.restoreRedraw();
    };
    syncToggleAll();
    const toggleAllLabel = labelled(container.dataset.toggleAllLabel, toggleAll);
    toggleAllLabel.classList.add('font-bold', 'border-b', 'border-gray-200', 'mb-1');

    container.replaceChildren(column_select, toggleAllLabel, ...checkboxes);

    column_select.onchange();

    this.table.filter = new Filter(
      this.table,
      this.table.columnSelect,
      this.table.filterSelect,
      this.table.filterInput,
      this.table.filterCount,
      this.table.filterList,
      this.table.addFilterButton,
      this.table.clearFilterButton,
      this.table.getColumns().map((c) => {
        return c.getField();
      })
    );
  }
}

// ########### URL Generator ###########
// we overwrite the default url generator to build correct array parameters for rails
function generateParamsList(data, prefix) {
  let output = [];

  prefix = prefix || '';

  if (Array.isArray(data)) {
    data.forEach((item, i) => {
      output = output.concat(generateParamsList(item, prefix ? `${prefix}[]` : i));
    });
  } else if (typeof data === 'object') {
    for (const key in data) {
      output = output.concat(generateParamsList(data[key], prefix ? `${prefix}[${key}]` : key));
    }
  } else {
    output.push({ key: prefix, value: data });
  }

  return output;
}

function serializeParams(params) {
  const output = generateParamsList(params);
  const encoded = [];

  for (const item of output) {
    encoded.push(`${encodeURIComponent(item.key)}=${encodeURIComponent(item.value)}`);
  }

  return encoded.join('&');
}

// transform the tabulator sort into ransack sort
function transformSort(params) {
  const sort = structuredClone(params.sort);
  delete params.sort;
  params.s = [];
  for (const obj of sort) {
    const mapping = this.options.mapper.reverseKey(obj.field);
    params.s.push(`${mapping.field} ${obj.dir}`);
  }

  return params;
}

function generateUrl(url, config, params) {
  if (url) {
    if (params && Object.keys(params).length) {
      if (params.sort) {
        params = transformSort.bind(this)(params);
      }
      if (!config.method || config.method.toLowerCase() === 'get') {
        config.method = 'get';

        url += (url.includes('?') ? '&' : '?') + serializeParams(params);
      }
    }
  }

  return url;
}

// ########### URL Generator ###########

// improve image formatter to ignore undefined values
// also images will be hidden behind a button,
// which triggers the fetch and decryption
Tabulator.extendModule('format', 'formatters', {
  image: (cell, formatterParams, onRendered) => {
    const el = document.createElement('button');
    el.style = 'border: 1px solid;padding: 2px;border-radius: 5px;';
    el.innerText = 'Load Image';

    el.onclick = async (event) => {
      // create button to load image
      let src = cell.getValue();

      if (src === undefined) {
        console.log('Undefined Source!');
        event.target.replaceWith(document.createElement('div'));
        return;
      }

      src = await window.vault.decryptMessage(await (await fetch(src)).text());

      if (src === null) {
        const replacementDiv = document.createElement('div');
        replacementDiv.textContent = 'NO DATA';
        event.target.replaceWith(replacementDiv);
        return;
      }

      if (formatterParams.urlPrefix) {
        src = formatterParams.urlPrefix + src;
      }

      if (formatterParams.urlSuffix) {
        src = src + formatterParams.urlSuffix;
      }

      let el = null;
      // some of our "images" are actually PDFs
      if (src.startsWith('data:application/pdf;')) {
        el = document.createElement('a');
        el.setAttribute('href', src);
        el.setAttribute('target', '_blank');
        el.classList.add('text-primary', 'hover:underline');
        el.innerText = 'Open PDF';
      } else {
        el = document.createElement('img');
        el.setAttribute('src', src);

        el.onclick = (e) => openImage(e.target.cloneNode());
      }

      switch (typeof formatterParams.height) {
        case 'number':
          el.style.height = `${formatterParams.height}px`;
          break;

        case 'string':
          el.style.height = formatterParams.height;
          break;
      }

      switch (typeof formatterParams.width) {
        case 'number':
          el.style.width = `${formatterParams.width}px`;
          break;

        case 'string':
          el.style.width = formatterParams.width;
          break;
      }

      el.addEventListener('load', () => {
        cell.getRow().normalizeHeight();
      });

      event.target.replaceWith(el);
    };

    return el;
  },
});

export default class extends Controller {
  static targets = [
    'editDescription',
    'filterSelect',
    'table',
    'control',
    'editButton',
    'cancelButton',
    'commitButton',
    'columnSelect',
    'columnSelectContainer',
    'exportConditions',
    'downloadSales',
    'filterInput',
    'filterCount',
    'filterList',
    'clearFilter',
    'addFilter',
    'exportFilter',
    'rowCount',
    'exportForm',
    'imageToggle',
  ];

  static values = {
    options: Object,
    url: String,
  };

  connect() {
    Tabulator.registerModule(SelectableColumnsModule);

    const optionsJson = this.element.dataset.tabulatorOptions;
    if (optionsJson && this.hasTableTarget) {
      const options = JSON.parse(optionsJson);
      options.mapper = new Mapper(options.mapper, options.data_category);

      this.table = new Tabulator(this.tableTarget, options);
      this.table.options.ajaxURLGenerator = generateUrl.bind(this.table);

      // TODO: maybe bind the controller to the table and access the targets directly
      this.table.editDescription = this.hasEditDescriptionTarget
        ? this.editDescriptionTarget
        : null;
      this.table.control_element = this.controlTarget;
      this.table.editButton = this.editButtonTarget;
      this.table.cancelButton = this.cancelButtonTarget;
      this.table.commitButton = this.commitButtonTarget;
      this.table.columnSelect = this.columnSelectTarget;
      this.table.downloadSales = this.hasDownloadSalesTarget ? this.downloadSalesTarget : null;
      this.table.columnSelectContainer = this.columnSelectContainerTarget;
      this.table.filterSelect = this.filterSelectTarget;
      this.table.filterInput = this.filterInputTarget;
      this.table.filterCount = this.filterCountTarget;
      this.table.filterList = this.filterListTarget;
      this.table.clearFilterButton = this.clearFilterTarget;
      this.table.addFilterButton = this.addFilterTarget;

      this.table.rowCountHint = this.hasRowCountTarget ? this.rowCountTarget : null;
      this.table.exportForm = this.hasExportFormTarget ? this.exportFormTarget : null;
      if (this.hasExportFilterTarget) {
        this.table.exportFilter = this.exportFilterTarget;
        this.table.getCurrentFilters = this.getCurrentFilters.bind(this);
      }

      window.myTabulatorInstance = this.table;
    }
  }

  // a FilterBarComponent above the table changed. The data tables pass its conditions on as tabulator remote
  // filters, which the out-API turns into ransack predicates (tabulator_query_to_ransacker); valueless ones need true.
  // view_data_rbf's Sales sends them as one json param (ProjectsController#sales_conditions_param) and reloads from
  // the first page. Its page URL carries the same param, so it can be shared or bookmarked as it is
  filter(event) {
    const { conditions } = event.detail;
    if (!('conditions' in this.table.options.ajaxParams)) {
      this.table.filter.remote_filter = conditions.map(({ field, type, value }) => ({
        field,
        type,
        value: value ?? true,
      }));
      this.table.filter.triggerFilter();
      return;
    }

    this.table.options.ajaxParams.conditions = JSON.stringify(conditions);
    this.table.setData();

    const url = new URL(window.location);
    if (conditions.length) url.searchParams.set('conditions', JSON.stringify(conditions));
    else url.searchParams.delete('conditions');
    // replace, not push: a history entry per token would make Back step through the filters
    history.replaceState(history.state, '', url);

    if (this.hasExportConditionsTarget)
      this.exportConditionsTarget.value = JSON.stringify(conditions);
  }

  getCurrentFilters() {
    // Get current filters from the tabulator
    const filters = [];

    // Get remote filters (applied to the API)
    if (this.table.filter?.remote_filter) {
      filters.push(...this.table.filter.remote_filter);
    }

    // Get local filters (applied to the data after loading)
    if (this.table.filter?.local_filter) {
      filters.push(...this.table.filter.local_filter);
    }

    // Get current sort order
    const sorters = this.table.getSorters().map((col) => `${col.field} ${col.dir}`);

    // Get current page size (for pagination)
    const pageSize = this.table.options.paginationSize || 25;

    return {
      filter: filters,
      s: sorters,
      pageSize: pageSize,
    };
  }
}
